import { Avatar, Box, Divider, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import theme from "../../theme";
import { Person } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { InvitationStatus, LoginType, ServiceOffer, serviceOptions, ServiceType, StudentGrade } from "../../common/const";
import CommonSelect from "../../common/components/CommonSelect";
import { setLoading } from "../../features/authSlice";
import { acceptInvitation, getMentorStudent } from "../../api/mentorApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { getStudentData, getUser } from "../../api/authApi";
import CommonDialog from "../../common/components/CommonDialog";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto', // Let it adjust based on content
    maxHeight: '600px', // Fixed maximum height
    overflow: 'auto', // Enable scrolling
}));

const StudentList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const { loading } = useSelector((state) => state.auth);
    const [users, setUsers] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [serviceType, setServiceType] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [userData, setUserData] = useState(null);
    const [currentGrade, setStudntGrade] = useState(null);

    const details = {
        token: location.search
        .split('token=')[1]
        ?.split('&')[0],
        student_id: searchParams.get('sid')
    };

    const getMentorStudentData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getMentorStudent(pageNo + 1, pageSize, serviceType ? JSON.stringify([{ "column": "service_offers", "op": "has", "value": serviceType }]) : '', '');
            setUsers(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            setUsers(null);
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getMentorStudentData();
    }, [pageNo, pageSize, serviceType]);


    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const getData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getStudentData(details?.student_id);
            if (response?.data?.user_id) {
                const userResponse = await getUser(response?.data?.user_id);
                setUserData(userResponse?.data);
                setStudntGrade(response?.student_current_grade)
                if (userResponse?.data?.avatar) {
                    if (userResponse?.data.login_type === LoginType.EMAIL) {
                        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${userResponse?.data.avatar}&download=false`, {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        });
                        const blob = await responseData.blob();
                        const imageUrl = URL.createObjectURL(blob);
                        setPreviewUrl(imageUrl || '');
                    } else {
                        setPreviewUrl(userResponse?.data.avatar);
                    }
                }
                setDialogOpen(true);
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (details?.student_id) {
            getData();
        }
    }, [details?.student_id]);

    const handleSave = async (status) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await acceptInvitation({
                status,
                token: details.token,
                service_offers: ServiceOffer.counsellor
            });
            if (response?.success) {
                showNotification(response?.message, 'success');
                navigate('/my-students');
                setDialogOpen(false);
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={dialogOpen}
                size="xs"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Accept Invitation</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box display="flex" gap={2} mb={2} mt={2} alignItems="center">
                        <Avatar
                            src={previewUrl}
                            sx={{
                                width: 80,
                                height: 80,
                                backgroundColor: theme.palette.grey[200],
                                cursor: 'pointer',
                            }}
                        >
                            <Person sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
                        </Avatar>
                        <Box>
                            <Typography fontWeight={600} fontSize={20}>{`${userData?.first_name} ${userData?.last_name}`}</Typography>
                            <Typography color="background.dark">{userData?.email}</Typography>
                            <Typography color="secondary.main">{StudentGrade?.find((a) => a.value === currentGrade)?.label}</Typography>
                        </Box>
                    </Box>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label='Reject'
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={() => {
                                handleSave(InvitationStatus.Reject);
                            }}
                        />
                        <CommonButton
                            label='Accept'
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={() => {
                                handleSave(InvitationStatus.Accept);
                            }}
                        />
                    </Fragment>
                )}
            />
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={600}>My Students</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                    <CommonSelect
                        name="serviceType"
                        value={serviceType}
                        onChange={(e) => {
                            setServiceType(e.target.value)
                        }}
                        placeholder="Select Service Type"
                        allowClear
                        options={ServiceType}
                        width="250px"
                    />
                </Box>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell width="20%">Student Name</Cell>
                                <Cell width="20%">Grade</Cell>
                                <Cell width="20%">Service Enrolled</Cell>
                                <Cell width="20%">Next Session</Cell>
                                <Cell width="20%">Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users?.students.length > 0 ? users?.students?.map((user, index) => (
                                <TableRow key={index}>
                                    <InnerCell>
                                        {user.student_name}
                                    </InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {user.grade ? StudentGrade.find((a) => a.value === user.grade)?.label : '-'}
                                    </InnerCell>
                                    <InnerCell>
                                        {user.service_enrolled ? serviceOptions.filter((a) => user.service_enrolled.includes(a.value)).map((a) => a.label)?.join(', ') : '-'}
                                    </InnerCell>
                                    <InnerCell>
                                        {user?.next_upcoming_meeting ? moment(user?.next_upcoming_meeting).format('YYYY-MM-DD') : '-'}
                                    </InnerCell>
                                    <InnerCell>
                                        <CommonButton
                                            label='View Profile'
                                            marginBottom="0px"
                                            height="30px"
                                            width="130px"
                                            color="white"
                                            background={theme.palette.secondary.main}
                                            handleClick={() => navigate(`/student-detail/${user?.student_id}&${user.service_enrolled.join(',')}&${user?.student_mentor_id}`)}
                                        />
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography>No Students Assign</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            {/* Pagination Component */}
            <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                    component="div"
                    count={users?.total || 0}
                    rowsPerPage={pageSize}
                    page={pageNo}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>
        </Fragment>
    )
}

export default StudentList;